import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Search } from 'lucide-react';

interface TableColumn<T> {
    key: string;
    label: React.ReactNode;
    sortable?: boolean;
    render?: (value: any, row: T, index: number) => React.ReactNode;
}

interface TableProps<T> {
    data: T[];
    columns: TableColumn<T>[];
    searchable?: boolean;
    searchPlaceholder?: string;
    itemsPerPage?: number;
    onRowClick?: (row: T) => void;
    getRowClassName?: (row: T) => string;
    onPageDataChange?: (pageData: T[]) => void;
}

interface SortConfig {
    key: string;
    direction: 'asc' | 'desc';
}

export function Table<T = any>({
    data,
    columns,
    searchable = false,
    searchPlaceholder = 'Search...',
    itemsPerPage = 10,
    onRowClick,
    getRowClassName,
    onPageDataChange,
}: TableProps<T>) {
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const filteredData = useMemo(() => {
        if (!searchable || !searchTerm) return data;

        return data.filter((item) => {
            if (typeof item !== 'object' || item === null) return false;
            return Object.values(item).some((value) =>
                String(value).toLowerCase().includes(searchTerm.toLowerCase())
            );
        });
    }, [data, searchTerm, searchable]);

    const sortedData = useMemo(() => {
        if (!sortConfig) return filteredData;

        return [...filteredData].sort((a, b) => {
            const aValue = (a as any)[sortConfig.key];
            const bValue = (b as any)[sortConfig.key];

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig]);

    const totalPages = Math.ceil(sortedData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = sortedData.slice(startIndex, startIndex + itemsPerPage);

    React.useEffect(() => {
        if (onPageDataChange) {
            onPageDataChange(paginatedData);
        }
    }, [paginatedData, onPageDataChange]);

    const handleSort = (key: string) => {
        setSortConfig((current) => ({
            key,
            direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const getSortIcon = (key: string) => {
        if (sortConfig?.key !== key) return null;
        return sortConfig.direction === 'asc' ? (
            <ChevronUp className="h-4 w-4" />
        ) : (
            <ChevronDown className="h-4 w-4" />
        );
    };

    return (
        <div className="bg-white rounded-xl shadow-soft overflow-hidden">
            {searchable && (
                <div className="p-4 border-b border-gray-200">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                    </div>
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-100">
                        <tr>
                            {columns?.map((column, index) => (
                                <th
                                    key={index}
                                    className={`px-6 py-3 text-left text-xs font-semibold text-primary-800 uppercase tracking-wider ${column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                                    onClick={() => column.sortable && handleSort(column.key)}
                                >
                                    <div className="flex items-center space-x-1">
                                        <span>{column.label}</span>
                                        {column.sortable && getSortIcon(column.key)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200">
                        {paginatedData?.map((item, index) => (
                            <tr
                                key={index}
                                className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''} ${getRowClassName ? getRowClassName(item) : ''}`}
                                onClick={() => onRowClick?.(item)}
                            >
                                {columns?.map((column, colIndex) => (
                                    <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {column.render ? column.render((item as any)[column.key], item, startIndex + index) : String((item as any)[column.key])}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="h-4 w-4" />
                            Previous
                        </button>
                        
                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(4, totalPages) }, (_, i) => {
                                let pageNum;
                                let showEllipsis = false;
                                
                                if (totalPages <= 4) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 2) {
                                    pageNum = i + 1;
                                    showEllipsis = i === 3 && totalPages > 4;
                                } else if (currentPage >= totalPages - 1) {
                                    pageNum = totalPages - 3 + i;
                                    showEllipsis = i === 0 && totalPages > 4;
                                } else {
                                    pageNum = currentPage - 1 + i;
                                    showEllipsis = (i === 0 && currentPage > 3) || (i === 3 && currentPage < totalPages - 2);
                                }
                                
                                return (
                                    <React.Fragment key={pageNum}>
                                        {showEllipsis && (
                                            <span className="px-2 text-gray-500">...</span>
                                        )}
                                        <button
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`w-8 h-8 p-0 text-sm border rounded-md ${
                                                currentPage === pageNum 
                                                    ? 'bg-blue-600 text-white border-blue-600' 
                                                    : 'border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                        
                        <button
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Table;
